import makerjs, {IModel, IPoint} from "makerjs";
import {createModelFromShape} from "../../../../source/js/shared/rendering/makerjs";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import mapValues from "lodash/mapValues";
import isEmpty from "lodash/isEmpty";
import invert from "lodash/invert";
import EnclosedShape, {Units} from "./../EnclosedShape";
import {renderLength} from "./../../functions/lengths";
import {centroid} from "./../../functions/makerjs";
import DimensionLine, { DimensionLineType } from "@/Models/Lines/DimensionLine";
import OffsetLine, { OffsetLineType } from "../Lines/OffsetLine";
import GuideLine from "../Lines/GuideLine";

export interface Dimensions {
	height?: number;
	length?: number;
	width?: number;
	diameter?: number;
	inner_radius?: number;
	outer_radius?: number;
	side_length?: number;
	side_amount?: string;
	side_height?: number;
	base?: number;
	radius?: number;
	'top_right_radius'?: number;
	'bottom_right_radius'?: number;
	'bottom_left_radius'?: number;
	'top_left_radius'?: number;
	height_left?: number;
	height_right?: number;
	width_top?: number;
	width_bottom?: number;
	height_top?: number;
	height_bottom?: number;
	offset_left?: number;
	head_length?: number;
	head_width?: number;
	stem_length?: number;
	stem_width?: number;
	number_of_points?: string;
	use_individual_corners?: boolean;
	desired_length?: number;
	desired_dimension?: string;
}

interface LengthDimensions {
	height?: Length;
	length?: Length;
	width?: Length;
	diameter?: Length;
	inner_radius?: Length;
	outer_radius?: Length;
	side_length?: Length;
	side_height?: Length;
	side_amount?: string;
	base?: Length;
	radius?: Length;
	'top_right_radius'?: Length;
	'bottom_right_radius'?: Length;
	'bottom_left_radius'?: Length;
	'top_left_radius'?: Length;
	height_left?: Length;
	height_right?: Length;
	width_top?: Length;
	width_bottom?: Length;
	height_top?: Length;
	height_bottom?: Length;
	offset_left?: Length;
	head_length?: Length;
	head_width?: Length;
	stem_length?: Length;
	stem_width?: Length;
	number_of_points?: string;
	use_individual_corners?: boolean;
	desired_length?: Length;
	desired_dimension?: string;

}

export interface DimensionProperties {
	primary: keyof Dimensions;
	secondary?: keyof Dimensions;
	tertiary?: keyof Dimensions;
	quaternary?: keyof Dimensions;
}

/**
 * The AbstractShape class.
 */
export default abstract class AbstractShape {
	public model: IModel;

	public readonly shape: string;
	protected cleanedPrimitives: LengthDimensions = {};
	protected labelMappings: Record<string, string> = {};

	private irregularShapes = [
		'arrow',
		'half-circle',
		'isosceles-triangle',
		'quarter-circle',
		'right-angled-triangle',
		'right-angled-triangle-mirrored',
		'sloped-rectangle',
		'sloped-rectangle-flip-horizontal',
		'sloped-right-rectangle',
		'sloped-right-rectangle-flip-vertical',
		'star',
	];

	/**
	 * Constructs the shape.
	 *
	 * @param {string} shape The shape name.
	 * @param {Dimensions} dimensions The dimensions of the shape.
	 * @param {Object} modifiers The modifiers to use for the shape.
	 * @protected
	 */
	protected constructor( shape: string, dimensions?: object, modifiers?: object ) {
		this.shape = shape;

		if ( dimensions ) {
			this.parseDimensions( dimensions );
			this.parseModifiers( modifiers );
		}
	}

	/**
	 * Gets the shape.
	 */
	getShape(): string {
		return this.shape;
	}

	/**
	 * Gets the enclosing rectangle of the shape's model.
	 */
	getEnclosingRectangle(): EnclosedShape {
		return new EnclosedShape( this.model );
	}

	/**
	 * Gets the midpoint of the model.
	 */
	getMidPoint(): Length[] {
		if ( this.irregularShapes.includes( this.shape ) ) {
			return centroid( this.model );
		}

		return this.getOriginalEnclosedShape().center();
	}

	/**
	 * Creates a midpoint model.
	 */
	createMidPoint(): IModel {
		const centerDot = createModelFromShape( 'circle', { diameter: 0.05 } );

		makerjs.model.move( centerDot, this.getOriginalEnclosedShape().mapToUnits( this.getMidPoint(), Units.CM ) );

		return centerDot;
	}

	/**
	 * Rotates the model.
	 *
	 * @param {number} degrees The amount of degrees to rotate the model.
	 */
	rotate( degrees: number ): void {
		makerjs.model.rotate( this.model, degrees, this.getOriginalEnclosedShape().mapToUnits( this.getMidPoint(), Units.CM ) );
	}

	/**
	 * Renders the model.
	 *
	 * @param {boolean} withMidPoint Whether to add the midpoint model.
	 */
	render( withMidPoint = false ): IModel {
		if ( withMidPoint ) {
			makerjs.model.addModel( this.model, this.createMidPoint(), 'modelMidPoint' );
		}

		return this.model;
	}

	/**
	 * Renders the dimension label.
	 *
	 * @param {string} dimension The dimension to render.
	 * @param {number} value The value to add to the label.
	 */
	renderDimensionLabel( dimension: string, value = undefined ): string {
		if ( value === undefined ) {
			value = this.cleanedPrimitives[ this.getDimensionLabel( dimension ) ];
		}

		value = ( value instanceof Length ) ? value.in_cm() : value;

		return window.measurementSelectorVariables.i18n.dimensionNames[ dimension ] + ' - ' + renderLength( value );
	}

	/**
	 * Determines if the model has defined paths.
	 */
	hasPaths(): boolean {
		return Object.keys( this.model.paths ).length > 0;
	}

	/**
	 * Parses the dimensions.
	 *
	 * @param {Object} dimensions The dimensions.
	 * @protected
	 */
	protected parseDimensions( dimensions ): void {
		for ( const property in dimensions[ this.shape ] ) {
			if ( property === 'number_of_points' || property === 'side_amount' ) {
				this.cleanedPrimitives[ property ] = dimensions[ this.shape ][ property ];
				continue;
			}

			this.cleanedPrimitives[ property ] = Length.from_cm( parseFloat( dimensions[ this.shape ][ property ] ) );
		}
	}

	/**
	 * Parses the modifiers.
	 *
	 * @param {Object} modifiers The modifiers.
	 * @protected
	 */
	protected parseModifiers( modifiers ): void {
		for ( const property in modifiers ) {
			if ( [ 'desired_dimension', 'font', 'text' ].includes( property ) ) {
				this.cleanedPrimitives[ property ] = modifiers[ property ];
				continue;
			}

			if ( property === 'use_individual_corners' ) {
				this.cleanedPrimitives[ property ] = modifiers[ property ] === true || modifiers[ property ] === 'true';
				continue;
			}

			this.cleanedPrimitives[ property ] = parseFloat( modifiers[ property ] );
		}
	}

	/**
	 * Creates a model instance of the shape.
	 */
	createModel(): void {
		const primitives = mapValues( this.cleanedPrimitives, ( primitive ) => primitive instanceof Length ? primitive.in_cm() : primitive );

		if ( isEmpty( primitives ) ) {
			return;
		}

		this.model = createModelFromShape( this.shape, primitives );
		this.model.type = this.shape;
	}

	getOriginalEnclosedShape(): EnclosedShape {
		return new EnclosedShape( this.model );
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width', secondary: 'length' };
	}

	getRatios(): Ratio[] {
		return [];
	}

	/**
	 * Gets the width of the model.
	 */
	abstract getWidth(): Length;

	/**
	 * Gets the lenght of the model.
	 */
	abstract getLength(): Length;

	abstract getDimensionLines(ratio: number, focusedElement: string, forceRender: boolean): DimensionLine[];

	getOffsetLines(offsets: Length[], center: IPoint, focusedElement = ''): OffsetLine[] {
		const centerX = center[0];
		const centerY = center[1];

		return [
			new OffsetLine([centerX - offsets[0].in_mm(), centerY], [centerX, centerY], renderLength(offsets[0].in_cm()), [0, 0], 'cutoutOffsetLeft' === focusedElement ? OffsetLineType.primary : OffsetLineType.default), // Horizontal line
			new OffsetLine([centerX, centerY - offsets[1].in_mm() ], [centerX, centerY], renderLength(offsets[1].in_cm()), [0, 0], 'cutoutOffsetBottom' === focusedElement ? OffsetLineType.primary : OffsetLineType.default), // Vertical line
		];
	}

	getGuideLines(focusedElement = ''): GuideLine[] {
		return [];
	}

	getDimensionLineType(id: string, focusedElement: string, forceRender = false): DimensionLineType {
		const prefix = focusedElement.includes("cutout_measurement") && !forceRender ? 'cutout_' : '';
		return focusedElement === `${prefix}${id}` ? DimensionLineType.primary : DimensionLineType.default;
	}

	abstract getDefaultDimensions(): Dimensions;
	abstract getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions;

	getDefaultModifiers(): object {
		return {};
	}

	getDefaultCutoutModifiers(): object {
		return {};
	}

	getHiddenDimensions(): string[] {
		return [];
	}

	getDimensionLabel( dimension: string ): string {

		// Get the mappings if they exist.
		if ( ! isEmpty( this.getLabelMappings() ) ) {

			/**
			 * Invert the mappings to get the proper 'old' label.
			 *
			 * @see https://app.asana.com/0/1130583511922775/1206582884714035/f
			 */
			const invertedMappings = invert( this.getLabelMappings() );

			return invertedMappings[ dimension ] ?? dimension;
		}

		return dimension;
	}

	getLabelMappings(): Record<string, string> {

		/**
		 * Holds mapping of labels to ensure proper retrieval.
		 *
		 * @see https://app.asana.com/0/1130583511922775/1206582884714035/f
		 */
		return this.labelMappings;
	}
}
